import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { CrmCallLog } from './callLog/CrmCallLog';
import { CrmCallQueue } from './callQueue/CrmCallQueue';
import { CrmContactDetail } from './contactDetail/CrmContactDetail';
import { CrmContacts } from './contacts/CrmContacts';
import { CrmEmailCampaign } from './email/CrmEmailCampaign';
import { CrmEmailCampaigns } from './email/CrmEmailCampaigns';
import { CrmEmailCompose } from './email/CrmEmailCompose';
import { CrmFollowUps } from './followUps/CrmFollowUps';
import { CrmImport } from './import/CrmImport';
import { CrmBatchUpdates } from './batch/CrmBatchUpdates';
import { CrmListDetail } from './lists/CrmListDetail';
import { CrmLists } from './lists/CrmLists';
import { CrmMatchReview } from './matchReview/CrmMatchReview';
import { CrmTags } from './tags/CrmTags';
import { CrmWorkflow } from './workflows/CrmWorkflow';
import { CrmWorkflowNew } from './workflows/CrmWorkflowNew';
import { CrmWorkflows } from './workflows/CrmWorkflows';
import { CRM_PATHS, CRM_ROOT, isCrmScreenLive } from './crmPaths';

interface CrmScreen {
  /** The screen's list path in CRM_PATHS: what CRM_PENDING and the sidebar key on. */
  screen: string;
  /** Relative to /crm. */
  path: string;
  element: React.ReactElement;
}

/**
 * Every CRM route. A detail page sits under its list's `screen`, so it is live
 * exactly when the list is. A new CRM screen needs a row here, a path in
 * crmPaths.ts (and in CRM_PENDING until its body is built) *and* an entry in
 * Sidebar.tsx.
 */
const SCREENS: Array<CrmScreen> = [
  { screen: CRM_PATHS.contacts, path: 'contacts', element: <CrmContacts /> },
  { screen: CRM_PATHS.contacts, path: 'contacts/:contactId', element: <CrmContactDetail /> },
  { screen: CRM_PATHS.lists, path: 'lists', element: <CrmLists /> },
  { screen: CRM_PATHS.lists, path: 'lists/:listId', element: <CrmListDetail /> },
  { screen: CRM_PATHS.tags, path: 'tags', element: <CrmTags /> },
  { screen: CRM_PATHS.batchUpdates, path: 'batch-updates', element: <CrmBatchUpdates /> },
  { screen: CRM_PATHS.matchReview, path: 'match-review', element: <CrmMatchReview /> },
  { screen: CRM_PATHS.import, path: 'import', element: <CrmImport /> },
  { screen: CRM_PATHS.callQueue, path: 'call-queue', element: <CrmCallQueue /> },
  { screen: CRM_PATHS.followUps, path: 'follow-ups', element: <CrmFollowUps /> },
  { screen: CRM_PATHS.callLog, path: 'calls', element: <CrmCallLog /> },
  { screen: CRM_PATHS.email, path: 'email', element: <CrmEmailCampaigns /> },
  { screen: CRM_PATHS.email, path: 'email/new', element: <CrmEmailCompose /> },
  { screen: CRM_PATHS.email, path: 'email/:campaignId', element: <CrmEmailCampaign /> },
  { screen: CRM_PATHS.workflows, path: 'workflows', element: <CrmWorkflows /> },
  { screen: CRM_PATHS.workflows, path: 'workflows/new', element: <CrmWorkflowNew /> },
  { screen: CRM_PATHS.workflows, path: 'workflows/:workflowId', element: <CrmWorkflow /> },
];

/**
 * Where /crm and unknown paths under it go: the contact list, or the first
 * live screen when that is not built yet, or home when none is. Never a path
 * that is not routed, which would bounce off this catch-all forever.
 */
export const crmLanding = (live: (screen: string) => boolean = (screen) => isCrmScreenLive(screen)) =>
  [CRM_PATHS.contacts, ...Object.values(CRM_PATHS)].find((screen) => live(screen)) ?? '/';

/**
 * Everything under /crm. Paths are relative to /crm; crmPaths.ts holds the
 * absolute ones for links.
 */
export const CrmRoutes = () => {
  const landing = crmLanding();

  return (
    <Routes>
      <Route index element={<Navigate to={landing} replace={true} />} />
      {SCREENS.filter(({ screen }) => isCrmScreenLive(screen)).map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
      <Route path="*" element={<Navigate to={landing} replace={true} />} />
    </Routes>
  );
};

/**
 * The section's entry in DashboardRoutes' table, called inline there as
 * `{crmDashboardRoute()}`. The CRM is part of every build, with no setting to
 * turn it off; in production only its built screens are routed (CRM_PENDING).
 */
export const crmDashboardRoute = () => <Route path={`${CRM_ROOT}/*`} element={<CrmRoutes />} />;
